import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Button, { buttonClasses } from "@mui/material/Button";
import { Link as RouterLink } from "react-router-dom";

/** px — the chevron's box. The icon's `fontSize` below has to resolve to this. */
const ICON_SIZE = 12;

/**
 * px — the chevron's path starts a quarter of the way into its 24-unit viewBox, so its
 * ink sits this far inside its own box.
 */
const CHEVRON_INK_INSET = ICON_SIZE / 4;

/** px — the horizontal half of the tap target, cancelled again by the pull-back below. */
const PADDING_INLINE = 12;

/**
 * The in-page way back.
 *
 * There is no top bar at any width, so every screen below the tab bar's own
 * destinations carries one of these instead of relying on browser chrome.
 */
export function BackLink({
  to,
  label,
  testId,
  onBack,
}: {
  to: string;
  label: string;
  /** Defaults to a generic id; pass one when a spec needs to target this link. */
  testId?: string;
  /**
   * Pop history instead of navigating to `to`.
   *
   * For a screen that is reachable from several places, where a fresh push to one
   * fixed path would be the wrong answer for the other callers — and would drop the
   * location state the screen behind this one is holding. `to` stays the fallback for
   * when there is no in-app entry to pop, so the control always leads somewhere.
   */
  onBack?: () => void;
}) {
  const asButton = onBack
    ? { component: "button" as const, type: "button" as const, onClick: onBack }
    : { component: RouterLink, to };

  return (
    <Button
      {...asButton}
      startIcon={<ArrowBackIosNewIcon sx={{ fontSize: `${ICON_SIZE}px` }} />}
      data-testid={testId ?? "back-link"}
      sx={{
        alignSelf: "flex-start",
        paddingInline: `${PADDING_INLINE}px`,
        // The button keeps its padding — that is the tap target — but is pulled back out
        // of it, so what lines up with the page gutter is the chevron itself rather than
        // the invisible box around it. Left as-is, the link reads as indented against
        // the cover image and the headline below it.
        marginInlineStart: `-${PADDING_INLINE + CHEVRON_INK_INSET}px`,
        color: "text.secondary",
        // MUI nudges the start icon by -4px, which would shift the alignment above by an
        // amount this component does not own.
        [`& .${buttonClasses.startIcon}`]: { marginLeft: 0 },
      }}
    >
      {label}
    </Button>
  );
}
